import type { CreateOrderRequest } from '../../Generated/CreateOrderRequest.ts'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.ts'
import type { SequencingPlatformKey } from '../../Generated/SequencingPlatform.ts'
import { SequencingPlatforms } from '../../Generated/SequencingPlatform.ts'
import type { TargetTaxonomyKey } from '../../Generated/TargetTaxonomy.ts'
import type { TargetRegionKey } from '../../Generated/TargetRegion.ts'
import type { ProcessingTypeKey } from '../../Generated/ProcessingType.ts'
import type { IOrganization } from '../../Model/User/IUser.ts'
import type { DiscoverySummary, PlannedOrderSettings, SettingChange, SettingSource } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { IOrder } from '../../Model/Order/IOrder.ts'
import {
  getMergedSampleConfigurationForPrimersRemoved,
  isProcessingTypeAllowedForPlatform,
  isSampleConfigurationAllowedForPlatform
} from '../../Util/sequencingPlatform.ts'
import { getAllowedTargetTaxaForProcessingType, isProcessingTypeTaxaCombinationValid } from '../../Util/processingType.ts'

/**
 * The organisation's defaults the wizard still takes: what no file can reveal. The sample format and the
 * target always come from the files, or from the lab's answer when the files do not tell.
 */
export type OrgDefaults = Partial<Pick<IOrganization, 'defaultRedlistMode' | 'defaultProcessingType'>>

/* never sent: the order is not created while the target is undetermined */
const UNDETERMINED_TAXON: TargetTaxonomyKey = 'OTHER'
const ORG_DEFAULT = "your organisation's default"

const platformOf = (discovery: Pick<DiscoverySummary, 'platform'>): SequencingPlatformKey | null =>
  discovery.platform === 'UNKNOWN' ? null : discovery.platform

/** The format for files whose layout is known; `undefined` while the reads do not say whether the primers are on. */
const formatFor = (merged: boolean, trimming: DiscoverySummary['trimming']): SampleConfigurationTypeKey | undefined => {
  if (trimming === 'UNKNOWN') {
    return undefined
  }
  if (merged) {
    return getMergedSampleConfigurationForPrimersRemoved(trimming === 'TRIMMED')
  }
  return trimming === 'TRIMMED' ? 'TRIMMED_SAMPLES' : 'SINGLE_SAMPLE_PAIRS'
}

/** The trimming verdict the plan may act on: a low-confidence "trimmed" could as well be an unregistered primer. */
export const settledTrimming = (discovery: Pick<DiscoverySummary, 'trimming' | 'classifier'>): DiscoverySummary['trimming'] =>
  discovery.classifier.confidence === 'low' ? 'UNKNOWN' : discovery.trimming

/**
 * `sheetPrimersAbsent`: the sheet's primers name a registered pair, which the registry scan looked for on
 * the reads and did not find — evidence for a "trimmed" that alone has low confidence.
 */
export const chooseSampleConfiguration = (
  discovery: DiscoverySummary,
  sheetPrimersAbsent = false
): PlannedOrderSettings['sampleConfiguration'] => {
  const platform = platformOf(discovery)
  const detected = discovery.classifier.configuration
  const low = discovery.classifier.confidence === 'low'
  if (detected !== 'UNKNOWN' && (!low || sheetPrimersAbsent) && isSampleConfigurationAllowedForPlatform(detected, platform)) {
    return low
      ? {
          value: detected,
          source: 'detected',
          via: 'sheet',
          reason: `${discovery.classifier.reason}; the sheet's registered primers are not on the reads`
        }
      : { value: detected, source: 'detected', reason: discovery.classifier.reason }
  }
  const layout = discovery.layout
  const trimming = settledTrimming(discovery)
  if (layout.value === 'UNKNOWN' && (layout.pairs === 0 || layout.unmated)) {
    return { value: 'SINGLE_SAMPLE_PAIRS', source: 'undetermined', reason: `no mates: ${layout.reason}` }
  }
  const merged = layout.value === 'MERGED_OR_SINGLE' || !isSampleConfigurationAllowedForPlatform('SINGLE_SAMPLE_PAIRS', platform)
  const value = formatFor(merged, trimming)
  if (!value) {
    return {
      value: merged ? 'MERGED_SAMPLES' : 'SINGLE_SAMPLE_PAIRS',
      source: 'undetermined',
      reason: low ? discovery.classifier.reason : 'the reads do not say whether the primers are still on'
    }
  }
  const multiplexUnclear = layout.value === 'PAIRED' && value === 'SINGLE_SAMPLE_PAIRS' && discovery.multiplexing === 'UNKNOWN'
  return {
    value,
    source: multiplexUnclear ? 'undetermined' : 'fallback',
    reason: `${detected === 'UNKNOWN' ? `format not detected (${discovery.classifier.reason})` : `${detected} does not fit ${platform}`}; chosen to fit the read layout`
  }
}

const specificOf = (amplicons: readonly TargetTaxonomyKey[]) => amplicons.filter((amplicon) => amplicon !== 'OTHER')

export const chooseTargetTaxonomy = (discovery: DiscoverySummary): PlannedOrderSettings['targetTaxonomy'] => {
  const primers = discovery.primers
  const registered =
    primers.status === 'found'
      ? { amplicons: primers.pair.amplicons, what: 'the primer pair is' }
      : primers.status === 'partial'
        ? { amplicons: primers.candidates.flatMap((pair) => pair.amplicons), what: `every registered pair with the ${primers.side} primer is` }
        : undefined
  if (registered) {
    const specific = [...new Set(specificOf(registered.amplicons))]
    if (specific.length === 1) {
      return { value: specific[0], source: 'detected', reason: `${registered.what} registered for ${specific[0]}` }
    }
    if (specific.length === 0) {
      return { value: 'OTHER', source: 'detected', reason: `${registered.what} registered for OTHER` }
    }
    return {
      value: UNDETERMINED_TAXON,
      source: 'undetermined',
      reason: `${registered.what} registered for several targets: ${specific.join(', ')}`
    }
  }
  return { value: UNDETERMINED_TAXON, source: 'undetermined', reason: `no target from the primers: ${primers.reason}` }
}

/** The region a target takes: the lab's for CO1 - Animal, else worldwide; none for any other target. */
export const regionOf = (
  target: Pick<PlannedOrderSettings['targetTaxonomy'], 'value' | 'source'>,
  entered: TargetRegionKey | undefined
): TargetRegionKey | undefined => (target.value === 'CO1_ANIMAL' && target.source !== 'undetermined' ? (entered ?? 'WORLDWIDE') : undefined)

type ProcessingPlan = Pick<PlannedOrderSettings, 'processingType' | 'processingTypeConflict'>

/**
 * The processing type for `preferred`: kept unless the platform rules it out (a silent fallback, nothing
 * to choose) or it does not cover the target (a question, never a silent switch).
 */
const processingTypeFor = (
  preferred: ProcessingTypeKey,
  platform: SequencingPlatformKey | null,
  target: PlannedOrderSettings['targetTaxonomy']
): ProcessingPlan => {
  if (!isProcessingTypeAllowedForPlatform(preferred, platform)) {
    const allowed = (platform && SequencingPlatforms[platform].allowedProcessingTypes?.[0]) || 'METABARCODING'
    return { processingType: { value: allowed, source: 'fallback', reason: `${platform} data cannot be processed as ${preferred}` } }
  }
  if (target.source !== 'undetermined' && !isProcessingTypeTaxaCombinationValid(preferred, target.value)) {
    return {
      processingType: {
        value: 'METABARCODING',
        source: 'fallback',
        reason: `${preferred} is not available for ${target.value} — waiting for your answer`
      },
      processingTypeConflict: {
        preferred,
        allowedTaxa: [...(getAllowedTargetTaxaForProcessingType(preferred) ?? [])] as TargetTaxonomyKey[]
      }
    }
  }
  return { processingType: { value: preferred, source: 'default', reason: ORG_DEFAULT } }
}

/** The order's settings from what the files said; only processing type, singletons and red list come from the organisation. */
export const planOrderSettings = (discovery: DiscoverySummary, org: OrgDefaults): PlannedOrderSettings => {
  const targetTaxonomy = chooseTargetTaxonomy(discovery)
  return {
    sampleConfiguration: chooseSampleConfiguration(discovery),
    targetTaxonomy,
    ...processingTypeFor(org.defaultProcessingType ?? 'METABARCODING', platformOf(discovery), targetTaxonomy),
    targetRegion: regionOf(targetTaxonomy, undefined),
    allowSingletons: false,
    redlistMode: org.defaultRedlistMode ?? 'NONE'
  }
}

/**
 * The settings with another target the wizard settled on (from the sheet, or the lab's answer): the region
 * follows, and an organisation default processing type is judged against the new target again.
 */
export const withTargetTaxonomy = (
  settings: PlannedOrderSettings,
  target: PlannedOrderSettings['targetTaxonomy'],
  discovery: Pick<DiscoverySummary, 'platform'> | undefined
): PlannedOrderSettings => {
  const next = {
    ...settings,
    targetTaxonomy: target,
    targetRegion: regionOf(target, settings.targetRegionEntered)
  }
  const { processingType, processingTypeConflict } = settings
  if (processingType.source !== 'default' && !processingTypeConflict) {
    return next
  }
  const preferred = processingTypeConflict?.preferred ?? processingType.value
  const { processingType: planned, processingTypeConflict: conflict } = processingTypeFor(
    preferred,
    discovery ? platformOf(discovery) : null,
    target
  )
  return { ...next, processingType: planned, processingTypeConflict: conflict }
}

const LAB_SOURCES: readonly SettingSource[] = ['answered', 'entered']
const fromLab = (setting: { source: SettingSource }) => LAB_SOURCES.includes(setting.source)

/** Settings planned afresh from the files, with every one the lab answered or changed kept as the lab left it. */
export const keepLabSettings = (
  planned: PlannedOrderSettings,
  previous: PlannedOrderSettings,
  discovery: Pick<DiscoverySummary, 'platform'> | undefined
): PlannedOrderSettings => {
  let next: PlannedOrderSettings = {
    ...planned,
    targetRegionEntered: previous.targetRegionEntered,
    targetRegion: regionOf(planned.targetTaxonomy, previous.targetRegionEntered)
  }
  if (fromLab(previous.targetTaxonomy)) {
    next = withTargetTaxonomy(next, previous.targetTaxonomy, discovery)
  }
  if (fromLab(previous.processingType)) {
    next = { ...next, processingType: previous.processingType, processingTypeConflict: undefined }
  }
  if (fromLab(previous.sampleConfiguration)) {
    next = { ...next, sampleConfiguration: previous.sampleConfiguration }
  }
  if (previous.allowSingletonsSource === 'entered') {
    next = { ...next, allowSingletons: previous.allowSingletons, allowSingletonsSource: 'entered' }
  }
  return next
}

export const toCreateOrderRequest = (
  name: string,
  settings: PlannedOrderSettings,
  description?: string
): Omit<CreateOrderRequest, 'type'> => ({
  name: name.trim(),
  ...(description?.trim() ? { description: description.trim() } : {}),
  targetTaxonomy: settings.targetTaxonomy.value,
  targetRegion: settings.targetRegion,
  allowSingletons: settings.allowSingletons,
  sampleConfiguration: settings.sampleConfiguration.value,
  processingType: settings.processingType.value,
  redlistMode: settings.redlistMode
})

const ENTERED = 'entered by you'

/** The settings with the lab's change; a target or processing type the lab picks settles any conflict between them. */
export const withSettingChange = (settings: PlannedOrderSettings, change: SettingChange): PlannedOrderSettings => {
  switch (change.setting) {
    case 'targetTaxonomy':
      return {
        ...settings,
        targetTaxonomy: { value: change.value, source: 'entered', reason: ENTERED },
        targetRegion: regionOf({ value: change.value, source: 'entered' }, settings.targetRegionEntered),
        processingTypeConflict: undefined
      }
    case 'targetRegion':
      return { ...settings, targetRegionEntered: change.value, targetRegion: regionOf(settings.targetTaxonomy, change.value) }
    case 'processingType':
      return { ...settings, processingType: { value: change.value, source: 'entered', reason: ENTERED }, processingTypeConflict: undefined }
    case 'allowSingletons':
      return { ...settings, allowSingletons: change.value, allowSingletonsSource: 'entered' }
    case 'sampleConfiguration':
      return { ...settings, sampleConfiguration: { value: change.value, source: 'entered', reason: ENTERED } }
  }
}

/** The format with the primers the other way round, same layout; none for a multi-sample pair, which never switches. */
export const trimSwitchOf = (value: SampleConfigurationTypeKey): SampleConfigurationTypeKey | undefined => {
  switch (value) {
    case 'SINGLE_SAMPLE_PAIRS':
      return 'TRIMMED_SAMPLES'
    case 'TRIMMED_SAMPLES':
      return 'SINGLE_SAMPLE_PAIRS'
    case 'MERGED_SAMPLES':
      return 'MERGED_AND_TRIMMED_SAMPLES'
    case 'MERGED_AND_TRIMMED_SAMPLES':
      return 'MERGED_SAMPLES'
    default:
      return undefined
  }
}

/** The format with the reads the other way round (merged or R1/R2 pairs), primers the same; none for a multi-sample pair. */
export const layoutSwitchOf = (value: SampleConfigurationTypeKey): SampleConfigurationTypeKey | undefined => {
  switch (value) {
    case 'MERGED_SAMPLES':
      return 'SINGLE_SAMPLE_PAIRS'
    case 'MERGED_AND_TRIMMED_SAMPLES':
      return 'TRIMMED_SAMPLES'
    case 'SINGLE_SAMPLE_PAIRS':
      return 'MERGED_SAMPLES'
    case 'TRIMMED_SAMPLES':
      return 'MERGED_AND_TRIMMED_SAMPLES'
    default:
      return undefined
  }
}

/**
 * The region to write with the draft's target: the lab's choice, else the region a CO1 order already
 * holds (set in the classic view); others have none.
 */
export const regionFor = (order: Pick<IOrder, 'targetRegion'>, settings: PlannedOrderSettings) => {
  if (settings.targetTaxonomy.value !== 'CO1_ANIMAL') {
    return undefined
  }
  return settings.targetRegionEntered ?? order.targetRegion ?? settings.targetRegion ?? 'WORLDWIDE'
}

/** Whether the order already holds the settings the wizard lets the lab change, and the format a switch asks for. */
export const orderHasSettings = (
  order: Pick<IOrder, 'targetTaxonomy' | 'processingType' | 'allowSingletons'> & Partial<Pick<IOrder, 'sampleConfiguration' | 'targetRegion'>>,
  settings: PlannedOrderSettings,
  sampleConfiguration?: SampleConfigurationTypeKey
) =>
  order.targetTaxonomy === settings.targetTaxonomy.value &&
  order.processingType === settings.processingType.value &&
  !!order.allowSingletons === settings.allowSingletons &&
  (!sampleConfiguration || order.sampleConfiguration === sampleConfiguration) &&
  (!settings.targetRegionEntered || order.targetRegion === regionFor(order, settings))
